/**
 * Debug utilities for ingredient modal issues
 */
export function debugProductData(product, context = 'Unknown') {
    console.group(`🔍 Debug Product Data - ${context}`);
    if (!product) {
        console.error('❌ Product is null or undefined');
        console.groupEnd();
        return;
    }
    // Basic product info
    console.log('📋 Basic Info:');
    console.log('  ID:', product.id || product._id);
    console.log('  Name:', product.name);
    console.log('  Description:', product.description);
    // Price debugging
    console.log('💰 Price Info:');
    console.log('  price (euros):', product.price, typeof product.price);
    console.log('  priceCent:', product.priceCent, typeof product.priceCent);
    console.log('  Price is NaN?', isNaN(product.price));
    console.log('  PriceCent is NaN?', isNaN(product.priceCent));
    // Extra ingredients debugging
    console.log('🧄 Extra Ingredients:');
    if (product.extraIngredients && Array.isArray(product.extraIngredients)) {
        console.log('  Count:', product.extraIngredients.length);
        product.extraIngredients.forEach((extra, index) => {
            console.log(`  [${index}] Name: ${extra.name}`);
            console.log(`  [${index}] Category: ${extra.category} (type: ${typeof extra.category})`);
            console.log(`  [${index}] Price: ${extra.price} (type: ${typeof extra.price})`);
            console.log(`  [${index}] PriceCent: ${extra.priceCent} (type: ${typeof extra.priceCent})`);
        });
    }
    else {
        console.log('  Count: 0 (no extra ingredients or not an array)');
    }
    // Mandatory ingredients debugging
    console.log('✅ Mandatory Ingredients:');
    if (product.mandatoryIngredients && Array.isArray(product.mandatoryIngredients)) {
        console.log('  Count:', product.mandatoryIngredients.length);
        product.mandatoryIngredients.forEach((group, groupIndex) => {
            console.log(`  Group [${groupIndex}]:`);
            console.log(`    Name: ${group.name}`);
            console.log(`    MaxSelections: ${group.maxSelections}`);
            console.log(`    Ingredients count: ${group.ingredients?.length || 0}`);
            if (group.ingredients && Array.isArray(group.ingredients)) {
                group.ingredients.forEach((ing, ingIndex) => {
                    if (typeof ing === 'string') {
                        console.log(`      [${ingIndex}] ID only (not populated): ${ing}`);
                    }
                    else if (typeof ing === 'object') {
                        console.log(`      [${ingIndex}] ID: ${ing._id || ing.id}, Name: ${ing.name || 'MISSING'}`);
                    }
                });
            }
        });
    }
    else {
        console.log('  Count: 0 (no mandatory ingredients or not an array)');
    }
    // Size info debugging
    console.log('📏 Size Info:');
    console.log('  hasSizes:', product.hasSizes);
    console.log('  sizes exists:', !!(product.sizes));
    console.log('  sizes is array:', Array.isArray(product.sizes));
    if (product.sizes && Array.isArray(product.sizes)) {
        console.log('  Sizes count:', product.sizes.length);
        product.sizes.forEach((size, index) => {
            console.log(`    [${index}] ID: ${size._id || size.id}`);
            console.log(`    [${index}] Name: ${size.name}`);
            console.log(`    [${index}] Price: ${size.price}`);
            console.log(`    [${index}] PriceCent: ${size.priceCent}`);
        });
    }
    else {
        console.log('  Count: 0 (no sizes or not an array)');
    }
    // Categories debugging
    const categories = product.extraIngredients?.map((extra) => extra.category) || [];
    const uniqueCategories = [...new Set(categories)];
    console.log('📂 Categories:');
    console.log('  All categories:', categories);
    console.log('  Unique categories:', uniqueCategories);
    console.log('  Are categories IDs?', uniqueCategories.every((cat) => typeof cat === 'string' && cat.length === 24));
    // Other fields
    console.log('🔧 Other Fields:');
    console.log('  customizable:', product.customizable);
    console.log('  available:', product.available);
    console.log('  category:', product.category);
    console.groupEnd();
}
export function debugIngredientCategories(ingredientCategories, context = 'Unknown') {
    console.group(`🗂️ Debug Ingredient Categories - ${context}`);
    if (!ingredientCategories || !Array.isArray(ingredientCategories)) {
        console.error('❌ Ingredient categories is not an array');
        console.groupEnd();
        return;
    }
    console.log('📊 Count:', ingredientCategories.length);
    ingredientCategories.forEach((category, index) => {
        console.log(`[${index}] ID: ${category._id || category.id}`);
        console.log(`[${index}] Name: ${category.name}`);
    });
    console.groupEnd();
}
export function validateModalData(product) {
    const issues = [];
    if (!product) {
        issues.push('Product is null or undefined');
        return { isValid: false, issues };
    }
    // Price validation
    if (product.price === undefined || isNaN(product.price)) {
        if (product.priceCent === undefined || isNaN(product.priceCent)) {
            issues.push('No valid price found (neither price nor priceCent)');
        }
        else {
            issues.push('price field is invalid, but priceCent is available');
        }
    }
    // Extra ingredients validation
    if (product.extraIngredients && Array.isArray(product.extraIngredients)) {
        product.extraIngredients.forEach((extra, index) => {
            if (!extra.name)
                issues.push(`Extra ingredient [${index}] missing name`);
            if (extra.price === undefined || isNaN(extra.price)) {
                issues.push(`Extra ingredient [${index}] (${extra.name}) has invalid price`);
            }
            if (!extra.category)
                issues.push(`Extra ingredient [${index}] (${extra.name}) missing category`);
            if (typeof extra.category === 'string' && extra.category.length === 24) {
                issues.push(`Extra ingredient [${index}] (${extra.name}) category appears to be an ID, not a name`);
            }
        });
    }
    return {
        isValid: issues.length === 0,
        issues
    };
}
